import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Context from "./index";
import { useLocation } from "react-router-dom";
import { createPermissionSet } from "@/utils/permissions";
import {
  persistAccessTokenExpiry,
  clearAccessTokenExpiryStorage,
  parseJwtExpiryMs,
  readStoredAccessTokenExpiryMs,
} from "@/utils/tokenExpiry";

function initTokenFromStorage() {
  const access_token = localStorage.getItem("access_token");
  const refresh_token = localStorage.getItem("refresh_token");
  const isValid =
    access_token && access_token !== "null" && access_token !== "undefined";
  return isValid ? { access_token, refresh_token } : null;
}

function initUserFromStorage() {
  const stored = localStorage.getItem("user");
  if (!stored) return null;
  try {
    const parsedUser = JSON.parse(stored);
    const roleKeys = parsedUser.roles.map((r) => r.key);
    const systemIds = parsedUser.systems.map((s) => s.id);
    return {
      ...parsedUser,
      permissionSet: createPermissionSet(
        parsedUser?.all_permissions || [],
        [...systemIds, ...roleKeys]
      ),
    };
  } catch {
    localStorage.removeItem("user");
    return null;
  }
}

export const EMAIL_VERIFICATION_PENDING_KEY = "email_verification_pending";
export const PASSWORD_CHANGE_REQUIRED_KEY = "password_change_required_pending";

const readEmailVerificationPending = () =>
  localStorage.getItem(EMAIL_VERIFICATION_PENDING_KEY) === "true";

const readPasswordChangeRequired = () =>
  localStorage.getItem(PASSWORD_CHANGE_REQUIRED_KEY) === "true";

const AuthProvider = (props) => {
  const location = useLocation();
  const [token, setToken] = useState(initTokenFromStorage);
  const [user, setUser] = useState(initUserFromStorage);
  const [emailVerificationPending, setEmailVerificationPendingState] =
    useState(readEmailVerificationPending);
  const [passwordChangeRequired, setPasswordChangeRequiredState] = useState(
    readPasswordChangeRequired
  );
  const [isInternelServerError, setIsInternelServerError] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const isRefreshing = useRef(false);

  const setEmailVerificationPending = useCallback((pending) => {
    if (pending) {
      localStorage.setItem(EMAIL_VERIFICATION_PENDING_KEY, "true");
    } else {
      localStorage.removeItem(EMAIL_VERIFICATION_PENDING_KEY);
    }
    setEmailVerificationPendingState(!!pending);
  }, []);

  const setPasswordChangeRequired = useCallback((required) => {
    if (required) {
      localStorage.setItem(PASSWORD_CHANGE_REQUIRED_KEY, "true");
    } else {
      localStorage.removeItem(PASSWORD_CHANGE_REQUIRED_KEY);
    }
    setPasswordChangeRequiredState(!!required);
  }, []);

  useEffect(() => {
    setIsForbidden(false);
    setIsInternelServerError(false);
  }, [location.pathname]);

  // Ensure expiry is persisted for tokens that predate expiry storage
  useEffect(() => {
    const access_token = localStorage.getItem("access_token");
    if (access_token && access_token !== "null" && access_token !== "undefined") {
      if (!parseJwtExpiryMs(access_token) && !readStoredAccessTokenExpiryMs()) {
        persistAccessTokenExpiry(access_token);
      }
    }
  }, []);

  const addToken = useCallback((tokens) => {
    const { access_token, refresh_token, expires_in } = tokens;
    localStorage.setItem("access_token", access_token);
    persistAccessTokenExpiry(access_token, expires_in);

    if (refresh_token) {
      localStorage.setItem("refresh_token", refresh_token);
    }
    setToken(tokens);

    return tokens;
  }, []);

  const removeToken = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    clearAccessTokenExpiryStorage();
    localStorage.removeItem(EMAIL_VERIFICATION_PENDING_KEY);
    localStorage.removeItem(PASSWORD_CHANGE_REQUIRED_KEY);
    setEmailVerificationPendingState(false);
    setPasswordChangeRequiredState(false);
    setToken(null);

    return null;
  }, []);

  const addUser = useCallback((userData) => {
    const systemIds = userData.systems.map(system => system.id);
    const roleKeys = userData.roles.map(role => role.key);
    const permissionSet = createPermissionSet(userData?.all_permissions || [], [...systemIds, ...roleKeys]);
    const userWithRoles = {
      ...userData,
      permissionSet,
      roles: userData.roles || [{ name: "user" }],
    };

    localStorage.setItem("user", JSON.stringify(userWithRoles));
    setUser(userWithRoles);

    return userWithRoles;
  }, []);

  const updateUser = useCallback(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const roleKeys = parsedUser.roles.map(role => role.key);
        const systemIds = parsedUser.systems.map(system => system.id);
        const userWithRoles = {
          ...parsedUser,
          permissionSet: createPermissionSet(parsedUser?.all_permissions || [], [...systemIds, ...roleKeys]),
          roles: parsedUser.roles || [{ name: "user" }],
        };
        setUser(userWithRoles);
        return userWithRoles;
      } catch (error) {
        console.error("Failed to parse user data from localStorage:", error);
        localStorage.removeItem("user");
        setUser(null);
        return null;
      }
    } else {
      setUser(null);
    }

    return storedUser;
  }, []);

  const removeUser = useCallback(() => {
    localStorage.removeItem("user");
    setUser(null);

    return null;
  }, []);

  const contextValue = useMemo(
    () => ({
      token,
      addToken,
      removeToken,
      user,
      addUser,
      updateUser,
      removeUser,
      emailVerificationPending,
      setEmailVerificationPending,
      passwordChangeRequired,
      setPasswordChangeRequired,
      isInternelServerError,
      setIsInternelServerError,
      isForbidden,
      setIsForbidden,
      isRefreshing,
    }),
    [
      token,
      addToken,
      removeToken,
      user,
      addUser,
      updateUser,
      removeUser,
      emailVerificationPending,
      setEmailVerificationPending,
      passwordChangeRequired,
      setPasswordChangeRequired,
      isInternelServerError,
      isForbidden,
    ]
  );

  return (
    <Context.Provider value={contextValue}>
      {props.children}
    </Context.Provider>
  );
};

export default AuthProvider;
